import type { GeoFix, GeolocationPort } from '@/platform/geolocation'
import { createEngine, internals, reconfigure, step, view, type EngineInternals } from '../domain/engine'
import type { LatLng } from '../domain/geometry'
import type { DetectionProfile } from '../domain/profile'
import { allRulesOn, type EngineRules } from '../domain/rules'
import type { DetectionSite, EngineEvent, EngineInput, EngineState, EngineView, SiteStatus } from '../domain/types'

// Fait tourner le moteur sur la tablette : positions fréquentes (indépendantes de la télémétrie de 30 s), battement
// d'horloge chaque seconde, actions de l'opérateur et statuts confirmés par le serveur. Aucun envoi au serveur ici :
// les événements sortent par `setEventSink` (journal local et synchronisation, lot O4). État en mémoire seulement.

export interface DetectionSetup {
  profile: DetectionProfile
  /** Drapeau par résidence de la mission (snapshot); `null` : résidence hors détection. */
  flags: Record<string, LatLng | null>
}

export interface RunnerClock {
  now: () => number
  setInterval: (fn: () => void, ms: number) => number
  clearInterval: (id: number) => void
  uuid: () => string
}

export const browserRunnerClock: RunnerClock = {
  now: () => Date.now(),
  setInterval: (fn, ms) => window.setInterval(fn, ms),
  clearInterval: (id) => {
    window.clearInterval(id)
  },
  uuid: () => crypto.randomUUID(),
}

export type RunnerAction =
  | { kind: 'CHOOSE_SITE'; itemId: string }
  | { kind: 'CHOOSE_NONE' }

export interface DetectionSnapshot {
  view: EngineView
  /** Dernière position reçue (fiable ou non), pour la précision affichée et les distances. */
  lastFix: { point: LatLng; accuracyM: number | null; at: number } | null
  /** Décisions récentes, la plus récente en dernier. */
  decisions: EngineEvent[]
  /** Diagnostic de calibration (temporaire) : positions reçues et compteurs internes du moteur. */
  debug: EngineInternals & { fixCount: number }
}

const decisionKinds = new Set<EngineEvent['kind']>([
  'CAPTURED',
  'COMPLETED',
  'VISIT_NOT_ADMISSIBLE',
  'AMBIGUITY',
  'AMBIGUITY_CLEARED',
  'DETECTION_SUSPENDED',
  'DETECTION_RESUMED',
])
const keptDecisions = 20
const tickMs = 1_000

export class DetectionRunner {
  private state: EngineState
  private current: DetectionSnapshot
  private readonly listeners = new Set<(snapshot: DetectionSnapshot) => void>()
  private readonly serverStatuses: Record<string, SiteStatus>
  private stopWatch: (() => void) | null = null
  private eventSink: ((events: EngineEvent[]) => void) | null = null
  private timer: number | null = null

  constructor(
    setup: DetectionSetup,
    items: readonly { itemId: string; order: number; status: SiteStatus }[],
    private readonly geolocation: GeolocationPort,
    private readonly clock: RunnerClock = browserRunnerClock,
    rules: EngineRules = allRulesOn,
  ) {
    const sites: DetectionSite[] = items.map((item) => ({
      itemId: item.itemId,
      order: item.order,
      flag: setup.flags[item.itemId] ?? null,
      status: 'TODO',
    }))
    this.state = createEngine(setup.profile, sites, rules)
    this.serverStatuses = Object.fromEntries(items.map((item) => [item.itemId, 'TODO']))
    this.current = {
      view: view(this.state),
      lastFix: null,
      decisions: [],
      debug: { ...internals(this.state), fixCount: 0 },
    }
    this.syncStatuses(Object.fromEntries(items.map((item) => [item.itemId, item.status])))
  }

  /** Reçoit chaque événement du moteur dès sa production (journal local); aucun avant l'installation. */
  setEventSink(sink: ((events: EngineEvent[]) => void) | null): void {
    this.eventSink = sink
  }

  get snapshot(): DetectionSnapshot {
    return this.current
  }

  subscribe(listener: (snapshot: DetectionSnapshot) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  start(): void {
    if (this.stopWatch) return
    this.stopWatch = this.geolocation.watch(
      (fix) => {
        this.observe(fix)
      },
      () => {
        // Refus ou indisponibilité : le battement d'horloge constate la perte de signal.
      },
    )
    this.timer = this.clock.setInterval(() => {
      this.apply({ kind: 'TICK', now: this.clock.now() })
    }, tickMs)
  }

  stop(): void {
    this.stopWatch?.()
    this.stopWatch = null
    if (this.timer !== null) this.clock.clearInterval(this.timer)
    this.timer = null
  }

  /** Calibration sur le terrain (TEMPORAIRE) : nouveau profil ou interrupteurs, sans perdre la visite active. */
  reconfigure(profile: DetectionProfile, rules: EngineRules): void {
    this.state = reconfigure(this.state, profile, rules)
    this.current = { ...this.current, view: view(this.state), debug: { ...internals(this.state), fixCount: this.current.debug.fixCount } }
    for (const listener of this.listeners) listener(this.current)
  }

  setPaused(paused: boolean): void {
    if (paused !== this.state.paused) this.apply({ kind: paused ? 'PAUSE' : 'RESUME', now: this.clock.now() })
  }

  dispatch(action: RunnerAction): void {
    this.apply({ ...action, now: this.clock.now() })
  }

  /**
   * Faits confirmés par le serveur (maintien de 3 s, problème, effacement par le bureau). Seuls les changements du
   * serveur sont appliqués : en observation, une terminaison hypothétique du moteur n'est jamais annulée par un
   * serveur qui n'en sait rien.
   */
  syncStatuses(statuses: Record<string, SiteStatus>): void {
    for (const [itemId, status] of Object.entries(statuses)) {
      const previous = this.serverStatuses[itemId]
      if (previous === undefined || previous === status) continue
      this.serverStatuses[itemId] = status
      const now = this.clock.now()
      if (status === 'DONE') this.apply({ kind: 'COMPLETE_MANUALLY', now, itemId, reason: 'Confirmé au serveur' })
      else if (status === 'PROBLEM') this.apply({ kind: 'REPORT_PROBLEM', now, itemId })
      else if (previous === 'DONE') this.apply({ kind: 'REOPEN', now, itemId, reason: 'Rouverte au serveur' })
    }
  }

  private observe(fix: GeoFix) {
    const point = { lat: fix.latitude, lng: fix.longitude }
    const now = this.clock.now()
    this.current = {
      ...this.current,
      lastFix: { point, accuracyM: fix.accuracy, at: fix.timestamp },
      debug: { ...this.current.debug, fixCount: this.current.debug.fixCount + 1 },
    }
    this.apply({
      kind: 'OBSERVATION',
      now,
      observation: { id: this.clock.uuid(), at: fix.timestamp, ...point, accuracyM: fix.accuracy },
    })
  }

  private apply(input: EngineInput) {
    const result = step(this.state, input)
    this.state = result.state
    if (result.events.length > 0) this.eventSink?.(result.events)
    const decisions = result.events.filter((event) => decisionKinds.has(event.kind))
    const nextView = view(this.state)
    if (decisions.length === 0 && JSON.stringify(nextView) === JSON.stringify(this.current.view) && input.kind !== 'OBSERVATION') {
      return
    }
    this.current = {
      ...this.current,
      view: nextView,
      decisions: [...this.current.decisions, ...decisions].slice(-keptDecisions),
      debug: { ...internals(this.state), fixCount: this.current.debug.fixCount },
    }
    for (const listener of this.listeners) listener(this.current)
  }
}
